import { ListSubheader, MenuItem, Select } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { makeHasPermissionSelector, tenantSelector } from '../selector/authenticationSelector';
import { tenantSelected } from '../action/authenticationEvents';
import { LEGACY_TENANT_SLUGS, Tenant } from '../api/types';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { cn } from '../utils/utils';
import { useEffect, useMemo } from 'react';
import { useIsMaster } from '../common/useIsMaster';
import { useCanViewAllTenants } from '../common/useCanViewAllTenants';
import { useGetAllTenantsQuery } from '../api/account/accountApiSlice';
import { useSwitchTenant } from '../common/useSwitchTenant';
import { masterModeEntryPath } from '../common/masterModeEntry';

const MASTER_MODE_VALUE = '__master_mode__';
const PREFIX = 'TenantSwitcher';

const classes = {
    selectRoot: `${PREFIX}-selectRoot`,
};

const StyledSelect = styled(Select)(({ theme }) => ({
    [`& .${classes.selectRoot}`]: {
        width: 355,
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
    },
}));

const TenantSwitcher = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const tenant = useSelector(tenantSelector);
    const isMaster = useIsMaster();
    // Service (receptionist) users with multiple assigned tenants also get
    // the all-tenant overview; masters always do. The onboarded-tenant
    // fetch below stays master-only (backend-gated on the `master` role).
    const canViewAllTenants = useCanViewAllTenants();
    const isInMasterMode = location.pathname.startsWith('/master');
    const hasTenantKolula = useSelector(makeHasPermissionSelector('tenant:kolula'));
    const hasTenantDhu = useSelector(makeHasPermissionSelector('tenant:dhu'));
    const hasTenantVonoviaV2 = useSelector(makeHasPermissionSelector('tenant:vonovia-v2'));
    // Accept both spellings: the original Auth0 permission was misspelled
    // `tenant:seeizeit` and some legacy users still have it; new permissions
    // use the correct `tenant:seezeit`. See `useAccessibleTenants.ts:23-26`.
    const hasTenantSeezeitCorrect = useSelector(makeHasPermissionSelector('tenant:seezeit'));
    const hasTenantSeezeitLegacy = useSelector(makeHasPermissionSelector('tenant:seeizeit'));
    const hasTenantSeezeit = hasTenantSeezeitCorrect || hasTenantSeezeitLegacy;
    const hasTenantStrandbadPertisau = useSelector(makeHasPermissionSelector('tenant:strandbad-pertisau'));
    const hasTenantBadeseePetershagen = useSelector(makeHasPermissionSelector('tenant:badesee-petershagen'));
    const hasTenantKolulaKey = useSelector(makeHasPermissionSelector('tenant:kolula-key'));
    const hasTenantSegmentaV2 = useSelector(makeHasPermissionSelector('tenant:segmenta-v2'));

    const { t } = useTranslation();

    // Onboarded (operational-DB) tenants for master users only — these are
    // the self-service tenants that have no `tenant:<slug>` permission and so
    // never appear in the hardcoded legacy list above. Backend gates the
    // endpoint on the `master` role, so we also skip the call for non-masters.
    const { data: onboardedTenants } = useGetAllTenantsQuery(undefined, { skip: !isMaster });

    // Drop any operational entry whose slug collides with a hardcoded legacy
    // tenant. The two sets are expected disjoint, but a collision would
    // double-render the same slug and break the Select's single-value match.
    const operationalTenants = useMemo(
        () => (onboardedTenants ?? []).filter((entry) => !LEGACY_TENANT_SLUGS.has(entry.slug)),
        [onboardedTenants],
    );

    useEffect(() => {
        const savedTenant = localStorage.getItem('selectedTenant');
        if (savedTenant && savedTenant !== tenant) {
            dispatch(tenantSelected({ tenant: savedTenant as Tenant | null }));
        }
    }, [dispatch, tenant]);

    // The same switch the "Öffnen" action on the master page "Mandanten"
    // runs — one code path, so both land identically (useSwitchTenant).
    const switchTenant = useSwitchTenant();

    const handleSelectionChange = (value: string) => {
        if (value === MASTER_MODE_VALUE) {
            navigate(masterModeEntryPath(isMaster));
            return;
        }
        switchTenant((value as Tenant) || null);
    };

    const selectValue = isInMasterMode ? MASTER_MODE_VALUE : (tenant || '');

    return (
        <StyledSelect
            variant={'outlined'}
            classes={{ select: cn(classes.selectRoot, 'bg-background text-foreground') }}
            value={selectValue}
            onChange={(event) => handleSelectionChange(event.target.value as string)}
            displayEmpty={true}
        >
            <MenuItem value={''} disabled={true}>
                <em>
                    <strong>{t('side_bar.choose_tenant')}</strong>
                </em>
            </MenuItem>
            {canViewAllTenants && (
                <MenuItem value={MASTER_MODE_VALUE}>
                    <strong>{t('master.title')}</strong>
                </MenuItem>
            )}
            {hasTenantKolula && <MenuItem value={'kolula'}>kolula SUP</MenuItem>}
            {hasTenantDhu && <MenuItem value={'dhu'}>Dhu</MenuItem>}
            {hasTenantVonoviaV2 && <MenuItem value={'vonovia_v2'}>Vonovia</MenuItem>}
            {hasTenantSeezeit && <MenuItem value={'seezeit'}>Seezeit</MenuItem>}
            {hasTenantStrandbadPertisau && <MenuItem value={'strandbad-pertisau'}>Strandbad Pertisau</MenuItem>}
            {hasTenantBadeseePetershagen && <MenuItem value={'badesee-petershagen'}>Badesee Petershagen</MenuItem>}
            {hasTenantKolulaKey && <MenuItem value={'kolula-key'}>kolula KEY</MenuItem>}
            {hasTenantSegmentaV2 && <MenuItem value={'segmenta_v2'}>Segmenta</MenuItem>}
            {operationalTenants.length > 0 && (
                <ListSubheader>{t('side_bar.onboarded_tenants')}</ListSubheader>
            )}
            {/* The slug, not just the organisation name. Several tenants can
                share one legal entity — kolula runs its own stations that way —
                and a list of identical names is unusable, which pushed a
                distinguishing suffix into `organization_name` itself on
                production. That field is the tenant's LEGAL name: it prints in
                the Impressum, the privacy page and every customer mail, so it
                must carry nothing but the legal entity. The slug is already
                unique, already the booking-page address, and already here. */}
            {operationalTenants.map((entry) => (
                <MenuItem key={entry.id} value={entry.slug}>
                    <span className={'truncate'}>{entry.organizationName}</span>
                    <span className={'ml-2 shrink-0 text-xs text-foreground/50'}>
                        {entry.slug}
                    </span>
                    {/* #1379: closed by its owner — listed so support can
                        open it and reactivate it (Account → Organisation). */}
                    {entry.status?.toLowerCase() === 'archived' && (
                        <span className={'ml-2 shrink-0 text-xs text-foreground/50'}>
                            {`(${t('side_bar.tenant_closed_mark')})`}
                        </span>
                    )}
                </MenuItem>
            ))}
        </StyledSelect>
    );
};

export default TenantSwitcher;
